// g2-unenforced-conditions-observability.ts
//
// P25. `g2-plan-gate-decision.ts` claimed, in a comment, that the §8
// conditions it could not measure were "REPORTED (`unenforcedConditions`)
// rather than quietly treated as 'checked and clear'". Measured at the start
// of P25, that claim was false: grep returned the type declaration, the
// assignment, the comment asserting it, and a single unit test — and ZERO
// production readers. `ensureG2DecisionRecorded` consumed `decision.reasons`
// and dropped `decision.unenforcedConditions`, so a person approving or
// waiving G2 learned the reasons the gate fired but never learned that a
// spec condition had gone unmeasured entirely.
//
// Fourth instance of this project's swallowed-state shape (P20 Lỗi A, P21's
// dropped promote result, P24's dropped release-flow outcome) — and the
// first where a comment invoked a mechanism that did not exist. Hence the
// standing rule it produced: before writing a comment that leans on another
// mechanism, grep that the mechanism has a real caller.
//
// Two outputs, following P24, because they fail differently: the printed
// line reaches the person watching this run and dies with the scrollback;
// the journal entry reaches `status --json` and any later audit.
//
// DORMANT BY DESIGN. P25 closed `scope_boundary`, the last unmeasured §8
// condition, so `UNENFORCED_CONDITIONS` is empty and neither output fires
// today. That is the point: this is the channel a future §8 condition lands
// in, and a dormant channel is exactly what someone deletes as dead code —
// which is how `scope_boundary` went eight phases without being measured.
import { randomUUID } from "node:crypto";
import type { MissionStore } from "./mission-store.js";

/**
 * Operator-facing sentence naming the §8 conditions this controller cannot
 * measure, or `undefined` when every condition is enforced.
 *
 * Silence on the empty list is deliberate (P24's reasoning): a line printed
 * on every tick of every mission trains the reader to skip it, which is how
 * the one that matters gets scrolled past.
 */
export function describeUnenforcedG2Conditions(
  unenforcedConditions: readonly string[],
): string | undefined {
  if (unenforcedConditions.length === 0) return undefined;
  return (
    `G2: ${unenforcedConditions.length} điều kiện §8 CHƯA được đo — ` +
    `${unenforcedConditions.join(", ")}. ` +
    'Quyết định G2 dưới đây KHÔNG bao gồm điều kiện này; "không bắt buộc" ' +
    "ở đây không có nghĩa là đã kiểm và sạch."
  );
}

/**
 * Journals the unmeasured conditions so the gap outlives the terminal.
 * Nothing is written when the list is empty — journaling "nothing unmeasured"
 * once per tick per mission is noise that buries the entry that matters.
 */
export async function journalUnenforcedG2Conditions(
  store: Pick<MissionStore, "commit">,
  unenforcedConditions: readonly string[],
): Promise<void> {
  if (unenforcedConditions.length === 0) return;
  const operationId = `g2-unenforced-${randomUUID()}`;
  await store.commit([
    {
      event_id: randomUUID(),
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        operation_kind: "g2_unenforced_conditions",
        outcome: "reported",
        reason: unenforcedConditions.join(", "),
      },
    },
  ]);
}
